/**
 * The small drawings on the case study's answer cards, exactly as the Paper file "Deck 2.0 Guri" draws
 * them (page Flow, frames 1.1 and 2B). One idea per drawing, in the illustration tokens. Under the pointer
 * each acts its idea out once (`art.module.css`).
 */
import type { ReactNode } from 'react';
import m from './art.module.css';

const INK = 'var(--color-illustration)';
const FAINT = 'var(--color-illustration-faint)';
const ACCENT = 'var(--color-illustration-accent)';
const CARD = 'var(--color-card)';

/** A piece that moves on hover, plus how. */
const moves = (...how: string[]) => [m.part, ...how].join(' ');

function Art({ children }: { children: ReactNode }) {
  return (
    // `overflow: visible` so a part that moves on hover is never cut by the drawing's edge.
    <svg width="72" height="52" viewBox="0 0 72 52" fill="none" aria-hidden="true" style={{ overflow: 'visible' }}>
      {children}
    </svg>
  );
}

/** A browser window with a few lines of page, the frame several drawings start from. */
function Window({ lines = true }: { lines?: boolean }) {
  return (
    <>
      <rect x="0.75" y="0.75" width="70.5" height="50.5" rx="6" stroke={INK} strokeWidth="1.5" />
      <path d="M1 11h70" stroke={INK} strokeWidth="1.5" />
      <circle cx="7" cy="6" r="1.5" fill={INK} />
      <circle cx="12" cy="6" r="1.5" fill={INK} />
      {lines ? (
        <>
          <rect x="8" y="18" width="22" height="3" rx="1.5" fill={FAINT} />
          <rect x="8" y="25" width="30" height="3" rx="1.5" fill={FAINT} />
          <rect x="8" y="32" width="18" height="3" rx="1.5" fill={FAINT} />
        </>
      ) : null}
    </>
  );
}

const Speech = () => (
  <>
    <path
      d="M36 2.75c16.5 0 29.25 9.6 29.25 21.5S52.5 45.75 36 45.75c-3.2 0-6.3-.4-9.2-1.1L15 50.5l2.6-9.9C10.5 36.9 6.75 31.2 6.75 24.25c0-11.9 12.75-21.5 29.25-21.5Z"
      stroke={INK}
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <rect className={moves(m.say, m.fromLeft)} x="20" y="19" width="32" height="3" rx="1.5" fill={FAINT} />
    <rect className={moves(m.say, m.fromLeft)} x="20" y="26" width="22" height="3" rx="1.5" fill={FAINT} />
    <rect className={moves(m.say, m.fromLeft)} x="44" y="26" width="8" height="3" rx="1.5" fill={INK} />
  </>
);

export const ART = {
  feature: (
    <Art>
      <Window />
      <rect className={moves(m.press, m.fromLeft)} x="8" y="40" width="14" height="5" rx="2.5" fill={FAINT} />
      <rect className={moves(m.lift)} x="44" y="18" width="20" height="27" rx="3" fill={INK} />
    </Art>
  ),
  problem: (
    <Art>
      <Window />
      <rect x="8" y="40" width="14" height="5" rx="2.5" fill={FAINT} />
      <g className={moves(m.wobble)}>
        <rect x="45" y="20" width="18" height="22" rx="3" transform="rotate(-10 54 31)" fill={INK} />
      </g>
      <path className={moves(m.burst)} d="M41 17l-2-3M41 44l-2 3M66 15l2-3M66 46l2 3" stroke={INK} strokeWidth="1.5" strokeLinecap="round" />
    </Art>
  ),
  redesign: (
    <Art>
      <g className={moves(m.old)}>
        <rect x="0.75" y="8.75" width="50.5" height="42.5" rx="6" stroke={FAINT} strokeWidth="1.5" />
        <rect x="7" y="17" width="18" height="3" rx="1.5" fill={FAINT} />
        <rect x="7" y="24" width="12" height="3" rx="1.5" fill={FAINT} />
        <rect x="7" y="33" width="38" height="12" rx="2" fill={FAINT} />
      </g>
      <g className={moves(m.new)}>
        <rect x="12.75" y="0.75" width="50.5" height="42.5" rx="6" fill={CARD} stroke={INK} strokeWidth="1.5" />
        <rect x="19" y="9" width="20" height="3" rx="1.5" fill={INK} />
        <rect x="19" y="16" width="12" height="3" rx="1.5" fill={INK} />
        <rect x="19" y="25" width="17" height="12" rx="2" fill={INK} />
        <rect x="40" y="25" width="17" height="12" rx="2" fill={INK} />
      </g>
      <path
        className={moves(m.spark)}
        d="M64 2c.7 3.8 1.8 4.9 5.6 5.6-3.8.7-4.9 1.8-5.6 5.6-.7-3.8-1.8-4.9-5.6-5.6 3.8-.7 4.9-1.8 5.6-5.6Z"
        fill={ACCENT}
      />
      <path className={moves(m.spark)} d="M57 12c.4 2 1 2.6 3 3-2 .4-2.6 1-3 3-.4-2-1-2.6-3-3 2-.4 2.6-1 3-3Z" fill={ACCENT} />
    </Art>
  ),
  mistake: (
    <Art>
      <Window />
      <rect x="44" y="18" width="20" height="27" rx="3" fill={INK} />
      <path
        className={`${m.draw} ${m.crack}`}
        pathLength={1}
        d="M50 18l4 8-5 5 6 7-3 7"
        stroke={CARD}
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <g className={moves(m.patch)}>
        <rect x="56" y="36" width="12" height="12" rx="2" transform="rotate(-12 62 42)" fill={ACCENT} />
      </g>
    </Art>
  ),
  how: (
    <Art>
      <rect x="0.75" y="15.75" width="18.5" height="20.5" rx="4" stroke={INK} strokeWidth="1.5" />
      <rect x="5" y="22" width="10" height="3" rx="1.5" fill={FAINT} />
      <rect x="5" y="28" width="7" height="3" rx="1.5" fill={FAINT} />
      <path className={moves(m.step)} d="M22 26h8M27 23l3 3-3 3" stroke={INK} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="32.75" y="15.75" width="18.5" height="20.5" rx="4" stroke={INK} strokeWidth="1.5" />
      <rect x="37" y="22" width="10" height="3" rx="1.5" fill={FAINT} />
      <rect x="37" y="28" width="7" height="3" rx="1.5" fill={FAINT} />
      <path className={moves(m.stepNext)} d="M54 26h8M59 23l3 3-3 3" stroke={INK} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <rect className={moves(m.grow, m.fromBottom)} x="64" y="11" width="8" height="30" rx="4" fill={ACCENT} />
      <path className={moves(m.rise)} d="M68 2v6M66 5l2-3 2 3" stroke={ACCENT} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </Art>
  ),
  lesson: (
    <Art>
      <rect x="8.75" y="0.75" width="54.5" height="50.5" rx="6" stroke={INK} strokeWidth="1.5" />
      <path d="M8.75 12h54.5" stroke={FAINT} strokeWidth="1.5" />
      <rect x="18" y="19" width="6" height="6" rx="1.5" fill={ACCENT} />
      <path d="M19.5 22l1.8 1.8L24 20.5" stroke={CARD} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="29" y="20.5" width="26" height="3" rx="1.5" fill={INK} />
      <rect className={moves(m.box)} x="18" y="30" width="6" height="6" rx="1.5" stroke={INK} strokeWidth="1.3" />
      <path className={m.tick} pathLength={1} d="M19.5 33l1.8 1.8L24 31.5" stroke={CARD} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      <rect className={moves(m.learned)} x="29" y="31.5" width="20" height="3" rx="1.5" fill={FAINT} />
      <rect x="18" y="41" width="6" height="6" rx="1.5" stroke={INK} strokeWidth="1.3" />
      <rect x="29" y="42.5" width="24" height="3" rx="1.5" fill={FAINT} />
    </Art>
  ),
  decision: (
    <Art>
      <path d="M4 26h18" stroke={INK} strokeWidth="2" strokeLinecap="round" />
      <path d="M22 26c10 0 12-14 22-14h10" stroke={FAINT} strokeWidth="2" strokeLinecap="round" strokeDasharray="3 4" />
      <path className={`${m.draw} ${m.chosen}`} pathLength={1} d="M22 26c10 0 12 14 22 14h10" stroke={INK} strokeWidth="2" strokeLinecap="round" />
      <circle cx="22" cy="26" r="4" fill={CARD} stroke={INK} strokeWidth="1.5" />
      <circle cx="54" cy="12" r="3" fill={FAINT} />
      <g className={moves(m.pick)}>
        <circle cx="54" cy="40" r="6" fill={ACCENT} />
        <path d="M51.5 40l1.8 1.8L57 38.2" stroke={CARD} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      </g>
    </Art>
  ),
  speech: (
    <Art>
      <Speech />
    </Art>
  ),
  interview: (
    <Art>
      <rect x="0.75" y="0.75" width="70.5" height="50.5" rx="6" stroke={INK} strokeWidth="1.5" />
      <g className={moves(m.nod)}>
        <circle cx="31" cy="20" r="7" fill={INK} />
        <path d="M17 50.5c0-10 6.3-15 14-15s14 5 14 15" fill={INK} />
      </g>
      <rect className={moves(m.blink)} x="52" y="35" width="13" height="10" rx="2" fill={ACCENT} />
    </Art>
  ),
  portfolio: (
    <Art>
      <Window lines={false} />
      <rect className={moves(m.page)} x="8" y="18" width="56" height="18" rx="2" fill={FAINT} />
      <rect x="8" y="41" width="24" height="3" rx="1.5" fill={INK} />
      <rect className={moves(m.link, m.fromLeft)} x="36" y="41" width="10" height="3" rx="1.5" fill={ACCENT} />
    </Art>
  ),
  team: (
    <Art>
      <g className={moves(m.leanRight)}>
        <circle cx="14" cy="22" r="6" fill={FAINT} />
        <path d="M2 44c0-8 5-12 12-12s12 4 12 12" fill={FAINT} />
      </g>
      <g className={moves(m.leanLeft)}>
        <circle cx="58" cy="22" r="6" fill={FAINT} />
        <path d="M46 44c0-8 5-12 12-12s12 4 12 12" fill={FAINT} />
      </g>
      <circle cx="36" cy="16" r="8" fill={INK} />
      <path d="M20 46c0-11 7-17 16-17s16 6 16 17" fill={INK} />
      <circle className={moves(m.ping)} cx="43" cy="12" r="4" fill={ACCENT} />
    </Art>
  ),
  highlight: (
    <Art>
      <g className={moves(m.lift)}>
        <rect x="12.75" y="6.75" width="46.5" height="38.5" rx="6" stroke={INK} strokeWidth="1.5" />
        <rect x="20" y="16" width="20" height="4" rx="2" fill={INK} />
        <rect x="20" y="25" width="30" height="3" rx="1.5" fill={FAINT} />
      </g>
      <path
        className={moves(m.spark)}
        d="M50 10c.7 3.8 1.8 4.9 5.6 5.6-3.8.7-4.9 1.8-5.6 5.6-.7-3.8-1.8-4.9-5.6-5.6 3.8-.7 4.9-1.8 5.6-5.6Z"
        fill={ACCENT}
      />
    </Art>
  ),
  deepDive: (
    <Art>
      <rect className={moves(m.old)} x="10.75" y="0.75" width="40.5" height="50.5" rx="6" stroke={FAINT} strokeWidth="1.5" />
      <g className={moves(m.new)}>
        <rect x="20.75" y="4.75" width="40.5" height="46.5" rx="6" fill={CARD} stroke={INK} strokeWidth="1.5" />
        <rect x="28" y="13" width="18" height="3" rx="1.5" fill={INK} />
        <rect x="28" y="20" width="26" height="3" rx="1.5" fill={FAINT} />
        <rect x="28" y="27" width="22" height="3" rx="1.5" fill={FAINT} />
        <rect x="28" y="34" width="26" height="3" rx="1.5" fill={FAINT} />
        <rect x="28" y="41" width="14" height="3" rx="1.5" fill={ACCENT} />
      </g>
    </Art>
  ),
  productDesigner: (
    <Art>
      <rect x="2.75" y="0.75" width="24.5" height="50.5" rx="5" stroke={INK} strokeWidth="1.5" />
      <rect x="7" y="7" width="16" height="12" rx="2" fill={FAINT} />
      <rect x="7" y="23" width="16" height="3" rx="1.5" fill={FAINT} />
      <rect x="7" y="29" width="11" height="3" rx="1.5" fill={FAINT} />
      <rect className={moves(m.press, m.fromLeft)} x="7" y="41" width="16" height="5" rx="2.5" fill={ACCENT} />
      <path className={moves(m.step)} d="M31 26h8M36 23l3 3-3 3" stroke={INK} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="42.75" y="0.75" width="24.5" height="50.5" rx="5" stroke={INK} strokeWidth="1.5" />
      <circle className={moves(m.pick)} cx="55" cy="16" r="6" fill={FAINT} />
      <rect x="47" y="28" width="16" height="3" rx="1.5" fill={FAINT} />
      <rect x="47" y="34" width="10" height="3" rx="1.5" fill={FAINT} />
      <rect x="47" y="41" width="16" height="5" rx="2.5" fill={INK} />
    </Art>
  ),
  uxDesigner: (
    <Art>
      <rect x="0.75" y="0.75" width="46.5" height="50.5" rx="6" stroke={INK} strokeWidth="1.5" />
      <rect x="8" y="10" width="26" height="3" rx="1.5" fill={INK} />
      <rect x="8" y="18" width="32" height="3" rx="1.5" fill={FAINT} />
      <rect x="8" y="25" width="22" height="3" rx="1.5" fill={FAINT} />
      <rect x="8" y="32" width="16" height="3" rx="1.5" fill={FAINT} />
      <g className={moves(m.leanLeft)}>
        <circle cx="50" cy="32" r="11" fill={CARD} stroke={INK} strokeWidth="1.5" />
        <rect x="44" y="30" width="12" height="4" rx="2" fill={ACCENT} />
        <path d="M58.5 40.5L68 50" stroke={INK} strokeWidth="2.5" strokeLinecap="round" />
      </g>
    </Art>
  ),
  designLead: (
    <Art>
      <g className={moves(m.nod)}>
        <circle cx="12" cy="18" r="7" fill={INK} />
        <path d="M0 51c0-10 5.4-16 12-16s12 6 12 16" fill={INK} />
      </g>
      <rect x="28.75" y="4.75" width="42.5" height="34.5" rx="4" stroke={INK} strokeWidth="1.5" />
      <path d="M50 39.5v11" stroke={INK} strokeWidth="1.5" strokeLinecap="round" />
      <rect x="35" y="10" width="12" height="10" rx="2" fill={FAINT} />
      <rect className={moves(m.ping)} x="53" y="10" width="12" height="10" rx="2" fill={ACCENT} />
      <rect x="35" y="24" width="12" height="10" rx="2" fill={FAINT} />
      <rect x="53" y="24" width="12" height="10" rx="2" fill={FAINT} />
    </Art>
  ),
} satisfies Record<string, ReactNode>;

export type ArtId = keyof typeof ART;
